import { FC } from "react";
import { StepsProps } from "../../types";
import { Button } from "@/components/ui";
import { FormProvider } from "react-hook-form";
import useStep2 from "./useStep2";
import {
  FormCheckbox,
  FormInput,
  FormPhoneInput,
  FormSectionHeader,
  FormSelect,
  Loader,
} from "@/components";
import {
  ADDRESS,
  ADDRESS_1,
  ADDRESS_2,
  ALTERNATE_LOGIN_ID,
  CITY,
  CORRESPONDENCE_ADDRESS,
  EMAIL_ID_1,
  EMAIL_ID_2,
  FIRST_NAME,
  LANDLINE_NO,
  LAST_NAME,
  MOBILE_NO,
  PO_BOX,
  SHOW_CORRESPONDENCE_ADDRESS,
  STATE,
  USERNAME,
  ZIPCODE,
} from "./consts";

const Step2: FC<StepsProps> = ({ onNextClick }) => {
  const {
    form,
    onSubmit,
    requiredFields,
    isUpdateUserPending,
    stateList,
    isUserDetailsLoading,
    keys,
    isUserDetailsRefetching,
  } = useStep2({
    onNextClick,
  });
  const { errors } = form.formState;

  return isUserDetailsLoading || isUserDetailsRefetching ? (
    <Loader />
  ) : (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
          <div>
            <FormSectionHeader>User Info</FormSectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
              <FormInput
                name={USERNAME}
                control={form.control}
                label="User ID"
                placeholder="Please enter"
                error={errors[USERNAME]?.message}
                isRequired={requiredFields.includes(USERNAME)}
                disabled
                noSpace
              />
              <FormInput
                name={ALTERNATE_LOGIN_ID}
                control={form.control}
                label="Alternative Login ID"
                placeholder="Please enter"
                error={errors[ALTERNATE_LOGIN_ID]?.message}
                isRequired={requiredFields.includes(ALTERNATE_LOGIN_ID)}
                noSpace
              />
              <FormInput
                name={FIRST_NAME}
                control={form.control}
                label="First Name"
                placeholder="Please enter"
                error={errors[FIRST_NAME]?.message}
                isRequired={requiredFields.includes(FIRST_NAME)}
              />
              <FormInput
                name={LAST_NAME}
                control={form.control}
                label="Last Name"
                placeholder="Please enter"
                error={errors[LAST_NAME]?.message}
                isRequired={requiredFields.includes(LAST_NAME)}
              />
              <FormPhoneInput
                name={MOBILE_NO}
                control={form.control}
                label="Mobile No"
                placeholder="Please enter"
                error={errors[MOBILE_NO]?.message}
                isRequired={requiredFields.includes(MOBILE_NO)}
              />
              <FormPhoneInput
                name={LANDLINE_NO}
                control={form.control}
                label="Landline No"
                placeholder="Please enter"
                error={errors[LANDLINE_NO]?.message}
                isRequired={requiredFields.includes(LANDLINE_NO)}
              />
              <FormInput
                name={EMAIL_ID_1}
                control={form.control}
                label="Email ID 1"
                placeholder="Please enter"
                error={errors[EMAIL_ID_1]?.message}
                isRequired={requiredFields.includes(EMAIL_ID_1)}
              />
              <FormInput
                name={EMAIL_ID_2}
                control={form.control}
                label="Email ID 2"
                placeholder="Please enter"
                error={errors[EMAIL_ID_2]?.message}
                isRequired={requiredFields.includes(EMAIL_ID_2)}
              />
            </div>
          </div>
          <div className="mt-8">
            <FormSectionHeader>Address</FormSectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
              <FormInput
                name={`${ADDRESS}.${ADDRESS_1}`}
                control={form.control}
                label="Address 1"
                placeholder="Please enter"
                error={errors[`${ADDRESS}.${ADDRESS_1}`]?.message}
                isRequired={requiredFields.includes(`${ADDRESS}.${ADDRESS_1}`)}
              />
              <FormInput
                name={`${ADDRESS}.${ADDRESS_2}`}
                control={form.control}
                label="Address 2"
                placeholder="Please enter"
                error={errors[`${ADDRESS}.${ADDRESS_2}`]?.message}
                isRequired={requiredFields.includes(`${ADDRESS}.${ADDRESS_2}`)}
              />
              <FormSelect
                key={keys.state}
                name={`${ADDRESS}.${STATE}`}
                control={form.control}
                label="State"
                placeholder="Please select"
                error={errors[`${ADDRESS}.${STATE}`]?.message}
                isRequired={requiredFields.includes(`${ADDRESS}.${STATE}`)}
                options={stateList.map((data: any) => ({
                  label: data.name,
                  value: data.code,
                }))}
              />
              <FormInput
                key={keys.city}
                name={`${ADDRESS}.${CITY}`}
                control={form.control}
                label="City"
                placeholder="Please select"
                error={errors[`${ADDRESS}.${CITY}`]?.message}
                isRequired={requiredFields.includes(`${ADDRESS}.${CITY}`)}
                variant="char"
              />
              <FormInput
                name={`${ADDRESS}.${ZIPCODE}`}
                control={form.control}
                label="Zipcode"
                placeholder="Please enter"
                error={errors[`${ADDRESS}.${ZIPCODE}`]?.message}
                isRequired={requiredFields.includes(`${ADDRESS}.${ZIPCODE}`)}
                maxLength={10}
                variant="integerSpeChar"
              />
              <FormInput
                name={`${ADDRESS}.${PO_BOX}`}
                control={form.control}
                label="PO Box"
                placeholder="Please enter"
                error={errors[`${ADDRESS}.${PO_BOX}`]?.message}
                isRequired={requiredFields.includes(`${ADDRESS}.${PO_BOX}`)}
                minLength={3}
                maxLength={3}
                variant="integer"
              />
            </div>
          </div>
          <div className="mt-8">
            <FormCheckbox
              name={`${SHOW_CORRESPONDENCE_ADDRESS}`}
              control={form.control}
              label="Correspondence Address" 
            />
          </div>
          <div className="mt-8">
            {form.watch(`${SHOW_CORRESPONDENCE_ADDRESS}`) && (
              <div className="mt-8">
                <FormSectionHeader>Correspondence Address</FormSectionHeader>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px] mt-4">
                  <FormInput
                    name={`${CORRESPONDENCE_ADDRESS}.${ADDRESS_1}`}
                    label="Address 1"
                    placeholder="Please enter"
                    control={form.control}
                    isRequired
                    error={(errors as any)[`${CORRESPONDENCE_ADDRESS}`]?.[`${ADDRESS_1}`]?.message}
                  />
                  <FormInput
                    name={`${CORRESPONDENCE_ADDRESS}.${ADDRESS_2}`}
                    label="Address 2"
                    placeholder="Please enter"
                    control={form.control}
                    isRequired={false}
                    error={(errors as any)[`${CORRESPONDENCE_ADDRESS}`]?.[`${ADDRESS_2}`]?.message}
                  />
                  <FormSelect
                    key={keys.cState}
                    name={`${CORRESPONDENCE_ADDRESS}.${STATE}`}
                    control={form.control}
                    label="State"
                    placeholder="Please select"
                    isRequired
                    options={stateList?.map((data: any) => ({
                      label: data.name,
                      value: data.code,
                    }))}
                    error={(errors as any)[`${CORRESPONDENCE_ADDRESS}`]?.[`${STATE}`]?.message}
                  />
                  <FormInput
                    key={keys.cCity}
                    name={`${CORRESPONDENCE_ADDRESS}.${CITY}`}
                    control={form.control}
                    isRequired
                    label="City"
                    placeholder="Please select"
                    error={(errors as any)[`${CORRESPONDENCE_ADDRESS}`]?.[`${CITY}`]?.message}
                  />
                  <FormInput
                    name={`${CORRESPONDENCE_ADDRESS}.${ZIPCODE}`}
                    label="Zipcode"
                    placeholder="Please enter"
                    control={form.control}
                    isRequired
                    variant="integerSpeChar"
                    maxLength={10}
                    error={(errors as any)[`${CORRESPONDENCE_ADDRESS}`]?.[`${ZIPCODE}`]?.message}
                  />
                  <FormInput
                    name={`${CORRESPONDENCE_ADDRESS}.${PO_BOX}`}
                    label="PO Box"
                    placeholder="Please enter"
                    control={form.control}
                    variant="integer"
                    maxLength={3}
                    isRequired
                    error={(errors as any)[`${CORRESPONDENCE_ADDRESS}`]?.[`${PO_BOX}`]?.message}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-row justify-end items-center gap-3 py-3">
          <Button type="submit" disabled={isUpdateUserPending}>
            {isUpdateUserPending && <Loader variant="secondary" />}
            Save & Next
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default Step2;
